<template>
  <v-container>
    <h1 class="text-center">🚀 Onboarding</h1>
    <p class="text-center text-body-2">
      Richte in wenigen Schritten deinen Discord-Bot ein.
    </p>

    <v-stepper v-model="step" vertical>
      <v-stepper-step :complete="step > 1" step="1">
        Willkommen
      </v-stepper-step>
      <v-stepper-content step="1">
        <v-card outlined class="pa-4 mb-4">
          <p>
            Schön, dass du dabei bist! In diesem kurzen Assistenten lädst du den Bot auf
            deinen Discord-Server ein und richtest die Live-Ankündigungen ein: Sobald du auf
            Twitch live gehst, postet der Bot automatisch eine Nachricht in einem Kanal
            deiner Wahl.
          </p>
          <p class="mb-0">
            Weitere Funktionen (Ticket-System, Regelwerk, Rollen-Sync, ...) kannst du im
            Anschluss jederzeit über das Menü konfigurieren.
          </p>
        </v-card>
        <v-btn color="primary" :block="$vuetify.breakpoint.smAndDown" @click="step = 2">Los geht's</v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 2" step="2">
        Bot zu deinem Server hinzufügen
      </v-stepper-step>
      <v-stepper-content step="2">
        <v-card outlined class="pa-4 mb-4">
          <v-alert type="info" dense outlined class="mb-4">
            Du richtest den Bot gerade für den Twitch-Kanal
            <strong>{{ currentInstanz || 'Unbekannt' }}</strong> ein.
            Falls das nicht der richtige Kanal ist (z. B. weil du das Onboarding für
            einen Streamer als Support-Account durchführst), logge dich zuerst aus und
            wähle beim Login die richtige Botinstanz aus.
          </v-alert>
          <p>
            Klicke auf den Button und wähle in Discord den Server aus, auf dem der Bot
            aktiv werden soll. Du benötigst dafür die Berechtigung "Server verwalten" auf
            diesem Server.
          </p>
          <v-checkbox
            v-model="instanceConfirmed"
            :label="`Ja, ich richte den Bot gerade korrekt für '${currentInstanz || 'Unbekannt'}' ein.`"
            dense
            hide-details
            class="mb-4"
          ></v-checkbox>
          <v-btn
            color="primary"
            :block="$vuetify.breakpoint.smAndDown"
            :disabled="!instanceConfirmed"
            :href="instanceConfirmed ? inviteUrl : undefined"
            @click="onInviteClick"
          >
            <v-icon left>mdi-discord</v-icon>
            Bot einladen
          </v-btn>
          <p class="text-caption mt-4 mb-0">
            Schon eingeladen? Dann kannst du direkt weiter zu den Live-Ankündigungen.
          </p>
        </v-card>
        <v-btn text :block="$vuetify.breakpoint.smAndDown" @click="step = 1">Zurück</v-btn>
        <v-btn
          color="primary"
          :class="$vuetify.breakpoint.smAndDown ? 'mt-2' : 'ml-2'"
          :block="$vuetify.breakpoint.smAndDown"
          @click="step = 3"
        >
          Weiter
        </v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 3" step="3">
        Live-Ankündigungen einrichten
      </v-stepper-step>
      <v-stepper-content step="3">
        <v-card outlined class="pa-4 mb-4">
          <v-alert v-if="botNotConnected" type="warning" dense outlined>
            Es wurde noch kein Discord-Server mit deinem Account verbunden. Gehe zurück zu
            Schritt 2 und lade den Bot zuerst auf deinen Server ein.
          </v-alert>
          <v-row v-else>
            <v-col cols="12">
              <v-switch
                v-model="enabled"
                label="Ankündigung aktiviert"
              ></v-switch>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                :items="channels"
                item-text="name"
                item-value="id"
                label="Discord-Kanal"
                v-model="channelId"
                :loading="loadingSettings"
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-textarea
                label="Nachricht"
                hint="Platzhalter: {streamer}, {titel}, {spiel}, {url}."
                persistent-hint
                v-model="message"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card>
        <v-btn text :block="$vuetify.breakpoint.smAndDown" @click="step = 2">Zurück</v-btn>
        <v-btn
          v-if="!botNotConnected"
          color="primary"
          :class="$vuetify.breakpoint.smAndDown ? 'mt-2' : 'ml-2'"
          :block="$vuetify.breakpoint.smAndDown"
          :loading="saving"
          @click="saveAndContinue"
        >
          Speichern &amp; weiter
        </v-btn>
        <v-btn
          v-if="!botNotConnected"
          :class="$vuetify.breakpoint.smAndDown ? 'mt-2' : 'ml-2'"
          :block="$vuetify.breakpoint.smAndDown"
          outlined
          :disabled="!channelId"
          :loading="sendingTest"
          @click="sendTest"
        >
          Testnachricht senden
        </v-btn>
      </v-stepper-content>

      <v-stepper-step step="4">
        Fertig
      </v-stepper-step>
      <v-stepper-content step="4">
        <v-card outlined class="pa-4 mb-4">
          <p>🎉 Dein Discord-Bot ist eingerichtet!</p>
          <p class="mb-2">Weitere Funktionen für deinen Discord-Bot:</p>
          <v-list dense>
            <v-list-item link @click="$router.push({ name: 'ticket_system' })">
              <v-list-item-icon><v-icon>mdi-ticket-outline</v-icon></v-list-item-icon>
              <v-list-item-title>Ticket-System</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="$router.push({ name: 'rules' })">
              <v-list-item-icon><v-icon>mdi-book-open-variant</v-icon></v-list-item-icon>
              <v-list-item-title>Regelwerk</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="$router.push({ name: 'role_sync' })">
              <v-list-item-icon><v-icon>mdi-account-sync</v-icon></v-list-item-icon>
              <v-list-item-title>Rollen-Sync</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="$router.push({ name: 'temp_voice' })">
              <v-list-item-icon><v-icon>mdi-account-voice</v-icon></v-list-item-icon>
              <v-list-item-title>Temporäre Sprachkanäle</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
        <v-btn text :block="$vuetify.breakpoint.smAndDown" @click="step = 3">Zurück</v-btn>
        <v-btn
          color="primary"
          :class="$vuetify.breakpoint.smAndDown ? 'mt-2' : 'ml-2'"
          :block="$vuetify.breakpoint.smAndDown"
          @click="$router.push({ name: 'live_announcement' })"
        >
          Zu den Live-Ankündigungen
        </v-btn>
      </v-stepper-content>
    </v-stepper>

    <v-snackbar
      v-model="settingsSaved"
      timeout="3000"
      color="green"
    >
      Einstellungen gespeichert!
    </v-snackbar>
    <v-snackbar
      v-model="testResult.show"
      timeout="5000"
      :color="testResult.success ? 'green' : 'red'"
    >
      {{ testResult.message }}
    </v-snackbar>
  </v-container>
</template>

<script>

import BackendService from '@/services/BackendService';

export default {
  data() {
    return {
      step: 1,
      loadingSettings: false,
      settingsSaved: false,
      instanceConfirmed: false,
      enabled: false,
      channelId: null,
      message: '',
      channels: [],
      saving: false,
      sendingTest: false,
      botNotConnected: false,
      testResult: {
        show: false,
        success: false,
        message: '',
      },
    };
  },
  computed: {
    currentInstanz() {
      return this.$store.state.instanz;
    },
    inviteUrl() {
      const clientId = process.env.VUE_APP_DISCORD_BOT_CLIENT_ID;
      const redirectUri = process.env.VUE_APP_DISCORD_BOT_INVITE_REDIRECT_URL;
      const permissions = '286649360';
      return `https://discord.com/oauth2/authorize?client_id=${clientId}&scope=${encodeURIComponent('bot applications.commands')}&permissions=${permissions}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&state=onboarding`;
    },
  },
  mounted() {
    if (this.$route.query.step) {
      this.step = Number(this.$route.query.step);
    }
    this.loadingSettings = true;
    BackendService.loadLiveAnnouncementSettings().then((data) => {
      this.enabled = Number(data.data.settings.live_announce_enabled) === 1;
      this.channelId = data.data.settings.live_announce_channel_id;
      this.message = data.data.settings.live_announce_message || '';
      this.channels = data.data.channels;
    }).catch((error) => {
      if (error.response && error.response.status === 409) {
        this.botNotConnected = true;
      }
    }).finally(() => {
      this.loadingSettings = false;
    });
  },
  methods: {
    onInviteClick() {
      if (this.instanceConfirmed) {
        sessionStorage.setItem('dcInviteConfirmedInstanz', this.currentInstanz || '');
      }
    },
    saveAndContinue() {
      this.saving = true;
      BackendService.saveLiveAnnouncementSettings({
        live_announce_enabled: this.enabled ? 1 : 0,
        live_announce_channel_id: this.channelId,
        live_announce_message: this.message,
      }).then(() => {
        this.settingsSaved = true;
        this.step = 4;
      }).catch(() => {
        alert('nö!');
      }).finally(() => {
        this.saving = false;
      });
    },
    sendTest() {
      this.sendingTest = true;
      BackendService.sendTestLiveAnnouncement({
        live_announce_channel_id: this.channelId,
        live_announce_message: this.message,
      }).then((data) => {
        this.testResult = {
          show: true,
          success: data.data.success,
          message: data.data.success
            ? 'Testnachricht wurde gesendet! Schau in den Discord-Kanal.'
            : `Fehlgeschlagen: ${data.data.error}`,
        };
      }).catch(() => {
        this.testResult = {
          show: true,
          success: false,
          message: 'Fehlgeschlagen: Verbindung zum Server nicht möglich.',
        };
      }).finally(() => {
        this.sendingTest = false;
      });
    },
  },
};
</script>
